import type { RefObject } from 'react'

import { Button, Popover } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

export interface ConfirmCompletionProps {
  anchor: RefObject<HTMLElement | null>
  open: boolean
  title: string
  onConfirm: () => void
  onCancel: () => void
}

/**
 * La segunda confirmación antes de terminar una tarea.
 *
 * Terminar una tarea la saca de la solapa de abiertas: con un solo clic, un
 * roce sobre la casilla —o elegir "Hecho" en el estado por error— la hacía
 * desaparecer, y para encontrarla había que ir a "Hechas" sabiendo qué buscar.
 * Reabrir no pasa por acá: no saca nada de la vista.
 *
 * "Terminar" arranca con el foco: quien quería terminarla confirma con Enter,
 * y Escape o un clic afuera la deja como estaba.
 */
export function ConfirmCompletion({
  anchor,
  open,
  title,
  onConfirm,
  onCancel,
}: ConfirmCompletionProps) {
  const t = useT()

  return (
    <Popover
      anchor={anchor}
      open={open}
      onClose={onCancel}
      role="alertdialog"
      aria-label={t('tasks.confirmComplete.label', { tarea: title })}
      className="flex w-[260px] flex-col gap-2 p-3"
    >
      <p className="text-xs text-text-1">{t('tasks.confirmComplete.question', { tarea: title })}</p>
      <p className="text-2xs text-text-3">{t('tasks.confirmComplete.hint')}</p>
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>{t('common.action.cancel')}</Button>
        <Button
          variant="primary"
          autoFocus
          onClick={() => {
            onConfirm()
          }}
        >
          {t('tasks.confirmComplete.confirm')}
        </Button>
      </div>
    </Popover>
  )
}
